import React from 'react';
import cn from 'classnames';
import ReactPlayer from 'react-player';
import { useBoolean, useDidUpdateEffect } from '@odin-labs/components';
import { setRefFactory } from 'components/utils';
import { PlayerProgress, PlayerProps } from './types';
import { PlayerControls } from './PlayerControls';

type IOSVideoElement = {
  /** Indicates whether the video is displaying in fullscreen mode */
  webkitDisplayingFullscreen: boolean;
  /** Exits fullscreen mode. */
  webkitExitFullscreen: () => void;
};
const isFullScreenOnIOS = (
  videoElement: unknown,
): videoElement is IOSVideoElement => {
  return (videoElement as IOSVideoElement)?.webkitDisplayingFullscreen;
};

export const Player = React.forwardRef(
  (
    props: PlayerProps,
    ref: React.ForwardedRef<ReactPlayer>,
  ): React.ReactElement => {
    const {
      className,
      enableFastForward,
      disablePlaybackSpeed,
      customControls,
      hlsOptions,
      url,
      value,
      onChange,
      onProgress,
      onPlay,
      onError,
      playing,
      muted,
      loop,
      volume,
      playbackRate,
    } = props;
    const disableFastForward = !enableFastForward;

    const {
      value: areCustomControlsVisible,
      setTrue: showCustomControls,
      setFalse: hideCustomControls,
    } = useBoolean(false);

    const containerRef = React.useRef<HTMLDivElement>(null);

    const [isPlaying, setIsPlaying] = React.useState(false);
    const [isPiP, setIsPiP] = React.useState(false);

    const [isMuted, setIsMuted] = React.useState(muted);
    useDidUpdateEffect(() => {
      if (muted !== undefined) setIsMuted(muted);
    }, [muted]);

    const [volumeLevel, setVolumeLevel] = React.useState(
      volume != null ? volume : 1,
    );
    useDidUpdateEffect(() => {
      if (volume !== undefined) setVolumeLevel(volume);
    }, [volume]);

    const [currentPlaybackRate, setCurrentPlaybackRate] =
      React.useState<number>(playbackRate);
    useDidUpdateEffect(() => {
      if (playbackRate !== undefined) setCurrentPlaybackRate(playbackRate);
    }, [playbackRate]);

    const { fraction: played = 0, value: playedSeconds = 0 } = value ?? {};
    const progressRef = React.useRef<PlayerProgress>({ played, playedSeconds });
    const innerRef = React.useRef<ReactPlayer>();
    const videoRef = React.useRef<HTMLVideoElement>(null);

    // update progressRef when value.fraction changes
    useDidUpdateEffect(() => {
      progressRef.current = { played, playedSeconds };
    }, [played]);

    const onLeavePiPHandler = React.useCallback((): void => {
      setIsPiP(false);
    }, []);

    /** This handler disables fast-forward feature in the player */
    const onSeekingHandler: HTMLVideoElement['onseeking'] = React.useCallback(
      (ev) => {
        const videoElement = ev.target as HTMLVideoElement;
        const { playedSeconds: currentPlayedSeconds } = progressRef.current;
        if (videoElement.currentTime > currentPlayedSeconds) {
          videoElement.currentTime = currentPlayedSeconds;
        }
      },
      [],
    );

    /** This handler prevents playback speed from being changed by the user within the player */
    const onRateChangeHandler: HTMLVideoElement['onratechange'] =
      React.useCallback((ev) => {
        const videoElement = ev.target as HTMLVideoElement;
        if (videoElement.playbackRate !== 1) {
          videoElement.playbackRate = 1;
        }
      }, []);

    const setInnerRef = React.useCallback(
      (reactPlayer: ReactPlayer): void => {
        if (!reactPlayer && videoRef.current) {
          // destroy internalPlayer
          videoRef.current.removeEventListener('seeking', onSeekingHandler);
          videoRef.current.removeEventListener(
            'ratechange',
            onRateChangeHandler,
          );
          videoRef.current.removeEventListener(
            'leavepictureinpicture',
            onLeavePiPHandler,
          );
          videoRef.current = null;
        }
        innerRef.current = reactPlayer;
      },
      [onSeekingHandler, onRateChangeHandler, onLeavePiPHandler],
    );

    const setRef = setRefFactory<ReactPlayer>({
      innerRef: setInnerRef,
      outerRef: ref,
    });

    const onReadyHandler = React.useCallback(
      (reactPlayer: ReactPlayer): void => {
        const internalPlayer = reactPlayer?.getInternalPlayer();
        if (
          internalPlayer instanceof HTMLVideoElement &&
          videoRef.current !== internalPlayer
        ) {
          videoRef.current = internalPlayer;
          // initialize internalPlayer
          if (value?.fraction) {
            reactPlayer.seekTo(value?.fraction, 'fraction');
            progressRef.current.playedSeconds = reactPlayer.getCurrentTime();
          }
          if (disableFastForward) {
            videoRef.current.addEventListener('seeking', onSeekingHandler);
          }
          if (disablePlaybackSpeed) {
            videoRef.current.addEventListener(
              'ratechange',
              onRateChangeHandler,
            );
          }
          if (customControls) {
            videoRef.current.addEventListener(
              'leavepictureinpicture',
              onLeavePiPHandler,
            );
          }
        }
      },
      [
        value,
        disableFastForward,
        disablePlaybackSpeed,
        customControls,
        onSeekingHandler,
        onRateChangeHandler,
        onLeavePiPHandler,
      ],
    );

    const onProgressHandler: ReactPlayer['props']['onProgress'] =
      React.useCallback(
        (state) => {
          if (state.played > progressRef.current.played) {
            progressRef.current = state;
            const {
              played: currentPlayed,
              playedSeconds: currentPlayedSeconds,
            } = state;
            onChange?.({
              fraction: currentPlayed,
              value: currentPlayedSeconds,
            });
          }
          onProgress?.(state);
        },
        [onChange, onProgress],
      );

    const onPlayHandler = React.useCallback((): void => {
      setIsPlaying(true);
      onPlay?.();
    }, [onPlay]);

    const onPauseHandler = React.useCallback((): void => {
      setIsPlaying(false);
    }, []);

    const togglePlay = React.useCallback((): void => {
      if (videoRef.current) {
        if (videoRef.current.paused) {
          videoRef.current.play();
        } else {
          videoRef.current.pause();
        }
      }
    }, []);

    const onEndedHandler = React.useCallback((): void => {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else if (isFullScreenOnIOS(videoRef.current)) {
        videoRef.current.webkitExitFullscreen();
      }
    }, [document]);

    // Mouse hover: show immediately and keep visible while hovering
    const onContainerMouseEnterHandler: React.MouseEventHandler<HTMLDivElement> =
      React.useCallback((): void => {
        showCustomControls();
      }, [showCustomControls]);

    const onContainerMouseLeaveHandler: React.MouseEventHandler<HTMLDivElement> =
      React.useCallback((): void => {
        hideCustomControls();
      }, [hideCustomControls]);

    const onContainerClickHandler: React.MouseEventHandler<HTMLDivElement> =
      React.useCallback(
        (event): void => {
          // when clicking on the container but the playbackRate dropdown is open
          // we don't want to toggle play/pause, but just let the dropdown to close
          if (event.target === videoRef.current) {
            togglePlay();
          }
        },
        [customControls, togglePlay],
      );

    return (
      <div
        ref={customControls ? containerRef : undefined}
        className={cn('odin-relative', className)}
        onMouseEnter={customControls ? onContainerMouseEnterHandler : undefined}
        onMouseLeave={customControls ? onContainerMouseLeaveHandler : undefined}
        onClick={customControls ? onContainerClickHandler : undefined}
      >
        <ReactPlayer
          key={url?.toString() ?? 'no-video-url'}
          ref={setRef}
          url={url}
          onReady={onReadyHandler}
          onProgress={onProgressHandler}
          onEnded={onEndedHandler}
          onPlay={onPlayHandler}
          onPause={onPauseHandler}
          onError={onError}
          controls={!customControls}
          playing={playing}
          muted={isMuted}
          volume={volumeLevel}
          playbackRate={currentPlaybackRate}
          loop={loop}
          width="100%"
          height="100%"
          config={{
            file: {
              ...(hlsOptions && {
                forceHLS: true,
                hlsOptions,
                hlsVersion: 'latest',
              }),
              ...(customControls && {
                attributes: { onContextMenu: (e: Event) => e.preventDefault() },
              }),
            },
          }}
        />
        {customControls && (
          <PlayerControls
            containerRef={containerRef}
            videoRef={videoRef}
            isPlaying={isPlaying}
            isMuted={isMuted}
            isVisible={areCustomControlsVisible || !isPlaying}
            volumeLevel={volumeLevel}
            currentPlaybackRate={currentPlaybackRate}
            isPiP={isPiP}
            togglePlay={togglePlay}
            onMutedChange={setIsMuted}
            onVolumeChange={setVolumeLevel}
            onPlaybackRateChange={setCurrentPlaybackRate}
            onPiPChange={setIsPiP}
          />
        )}
      </div>
    );
  },
);
